import {
  Avatar,
  Button,
  Checkbox,
  Group,
  Popover,
  ScrollArea,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { Search, Users } from "lucide-react";
import { useState } from "react";

import { useListMembers } from "../../api/generated/endpoints/teams/teams";
import { memberName } from "../members";
import { useMembership } from "../membership";
import classes from "./CreatorFilter.module.css";

/** Live's chips beside the "+" and "All": the signed-in user's, then two more picked. */
const VISIBLE_PICKED = 2;

/** Live's avatar colors, one per user, the same on every visit. */
const COLORS = ["indigo", "blue", "teal", "yellow", "pink", "red", "violet", "cyan"] as const;

function colorOf(userId: string): (typeof COLORS)[number] {
  let sum = 0;
  for (const char of userId) sum += char.charCodeAt(0);
  return COLORS[sum % COLORS.length]!;
}

export interface CreatorFilterProps {
  teamId: string;
  /** The creators picked; none is everyone ("All"). */
  value: readonly string[];
  onChange: (userIds: string[]) => void;
}

/**
 * Live's creator filter over a gallery (`filter-by-user`, capture gaps/25): a round "+" that
 * opens the team's members to tick ("Search by name", "Unselect all", "N of M selected"), the
 * signed-in user's chip, which picks them alone, up to two more picked members' chips, and "All".
 * The "+" counts the picked members not shown.
 */
export function CreatorFilter({ teamId, value, onChange }: CreatorFilterProps) {
  const members = useListMembers(teamId).data ?? [];
  const me = useMembership(teamId)?.userId;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const mine = members.find((member) => member.user_id === me);
  const others = members.filter((member) => member.user_id !== me && value.includes(member.user_id));
  const shown = others.slice(0, VISIBLE_PICKED);
  const more = Math.max(0, others.length - VISIBLE_PICKED);
  const onlyMe = value.length === 1 && value[0] === me;
  const toggle = (userId: string) =>
    onChange(value.includes(userId) ? value.filter((id) => id !== userId) : [...value, userId]);
  const typed = query.trim().toLowerCase();
  const listed = members.filter(
    (member) =>
      typed === "" ||
      memberName(member).toLowerCase().includes(typed) ||
      (member.email ?? "").toLowerCase().includes(typed),
  );
  return (
    <div className={classes.row}>
      <Popover opened={open} onChange={setOpen} position="bottom" offset={8} shadow="md" width={270}>
        <Popover.Target>
          <UnstyledButton
            className={classes.chip}
            data-selected={(value.length > 0 && !onlyMe && more > 0) || undefined}
            aria-label="Show more users"
            aria-expanded={open}
            onClick={() => setOpen((now) => !now)}
          >
            <Avatar size={36} radius="xl" className={more > 0 ? classes.count : classes.users}>
              {more > 0 ? `+${more}` : <Users size={18} strokeWidth={1.5} />}
            </Avatar>
          </UnstyledButton>
        </Popover.Target>
        <Popover.Dropdown className={classes.dropdown}>
          <TextInput
            className={classes.search}
            aria-label="Search by name"
            placeholder="Search by name"
            leftSection={<Search size={18} aria-hidden />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
          <Group justify="space-between" gap="sm">
            <Button
              variant="text"
              className={classes.unselect}
              disabled={value.length === 0}
              onClick={() => onChange([])}
            >
              Unselect all
            </Button>
            <Text c="dimmed" size="sm">
              {value.length} of {members.length} selected
            </Text>
          </Group>
          <ScrollArea.Autosize mah={300} offsetScrollbars scrollbarSize={5} className={classes.list}>
            {listed.map((member) => (
              <label key={member.user_id} className={classes.member}>
                <Checkbox
                  checked={value.includes(member.user_id)}
                  onChange={() => toggle(member.user_id)}
                  aria-label={`Select ${memberName(member)}`}
                />
                <Avatar
                  size={32}
                  radius="xl"
                  src={member.picture_url}
                  name={memberName(member)}
                  color={colorOf(member.user_id)}
                />
                <Text size="sm">{memberName(member)}</Text>
              </label>
            ))}
            {listed.length === 0 ? (
              <Text c="dimmed" ta="center" py="md">
                No users found
              </Text>
            ) : null}
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>
      {me === undefined ? null : (
        <Chip
          name={mine === undefined ? "Me" : memberName(mine)}
          userId={me}
          picture={mine?.picture_url ?? null}
          selected={value.includes(me)}
          onClick={() => onChange(onlyMe ? [] : [me])}
        />
      )}
      {value.length === 0 || onlyMe
        ? null
        : shown.map((member) => (
            <Chip
              key={member.user_id}
              name={memberName(member)}
              userId={member.user_id}
              picture={member.picture_url}
              selected
              onClick={() => toggle(member.user_id)}
            />
          ))}
      <UnstyledButton
        className={classes.chip}
        data-selected={value.length === 0 || undefined}
        aria-label="Filter by All"
        onClick={() => onChange([])}
      >
        <span className={classes.all}>All</span>
      </UnstyledButton>
    </div>
  );
}

interface ChipProps {
  name: string;
  userId: string;
  picture: string | null;
  selected: boolean;
  onClick: () => void;
}

/** A member's round chip, named on hover, ringed while picked. */
function Chip({ name, userId, picture, selected, onClick }: ChipProps) {
  return (
    <Tooltip label={name} withArrow>
      <UnstyledButton
        className={classes.chip}
        data-selected={selected || undefined}
        aria-label={`Filter by ${name}`}
        aria-pressed={selected}
        onClick={onClick}
      >
        <Avatar size={36} radius="xl" src={picture} name={name} color={colorOf(userId)} className={classes.avatar} />
      </UnstyledButton>
    </Tooltip>
  );
}
